/**
 * Find Problems — a server-side data-quality scan over one or more layers.
 *
 * Nine checks (see `quality_checks.py` for the actual logic, which is where
 * every one of them really runs — this panel only collects parameters and
 * renders results). Five run with no configuration at all; the other four
 * need a small, one-time parameter the panel collects per layer, because
 * BlueView has no Esri-style "relationship class" to infer them from —
 * which field is supposed to line up with which other layer's field is
 * something only the person configuring the check can know.
 *
 * RESULTS, TWO WAYS AT ONCE
 * The flagged features are both listed (click one to zoom to it and read its
 * detail) AND dropped onto the map as a temporary layer, colour-coded by
 * severity — the same "leave a result on the map, not just in a list"
 * pattern Join Features and Density already use, via `onCreateTempLayer`.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Crosshair,
  Download,
  Loader2,
  Plus,
  X as RemoveIcon,
} from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { cfg, scopedLayers, type ToolContext } from './types';
import {
  EmptyHint,
  Field,
  FieldRow,
  Notice,
  ResultHeader,
  ResultRow,
  ScrollList,
  btnClass,
  inputClass,
} from './shared';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { computeBounds } from '../../../layers/localFile';
import { fetchLayerFieldNames, fetchNumericFieldNames } from '../../../tools/toolFields';
import {
  qualityCheckService,
  type QualityCheckSettings,
  type QualityProblem,
  type QualityRelationshipRule,
} from '../../../services/qualityCheckService';
import type { Layer, LayerRenderer } from '../../../types';

interface CheckDef {
  id: string;
  label: string;
  hint: string;
  /** Needs a per-layer parameter the system cannot infer on its own. */
  advanced?: boolean;
}

const CHECK_DEFS: CheckDef[] = [
  { id: 'suspicious_geometry', label: 'Suspicious geometry', hint: 'Invalid, empty, self-intersecting, zero-length/area, or out-of-range coordinates.' },
  { id: 'duplicate_assets', label: 'Duplicate assets', hint: 'Two or more features occupying the same geometry.' },
  { id: 'overlapping_features', label: 'Overlapping features', hint: 'Polygons that overlap each other (parcels, zoning, service areas…).' },
  { id: 'disconnected_pipeline', label: 'Disconnected pipeline / missing connectivity', hint: 'Line segments whose ends touch nothing else in this layer.' },
  { id: 'abnormal_asset_patterns', label: 'Abnormal asset patterns', hint: 'Numbers far outside the layer’s typical range; rare text values that look like typos.' },
  { id: 'missing_attributes', label: 'Missing attributes', hint: 'Required fields left blank.', advanced: true },
  { id: 'orphan_features', label: 'Orphan features', hint: 'A point not near anything in a reference layer — e.g. a valve off the pipe network.', advanced: true },
  { id: 'invalid_relationships', label: 'Invalid relationships', hint: 'A field’s value does not exist in a reference layer’s field.', advanced: true },
  { id: 'inconsistent_status', label: 'Inconsistent status', hint: 'A status outside an allowed list, or mismatched with a directly connected feature.', advanced: true },
];

const ADVANCED_CHECK_IDS = new Set(CHECK_DEFS.filter((c) => c.advanced).map((c) => c.id));

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#dc2626',
  warning: '#d97706',
  info: '#2563eb',
};
const SEVERITY_LABEL: Record<string, string> = { critical: 'Critical', warning: 'Warning', info: 'Info' };
const SEVERITY_ORDER: Record<string, number> = { critical: 0, warning: 1, info: 2 };

interface RuleRow extends QualityRelationshipRule {
  id: string;
}

/** Per-layer settings for the four checks that need a relationship the
 *  system cannot infer. Kept separate from the always-global tolerances
 *  (snap distance, overlap %) since field names are layer-specific but a
 *  metre tolerance is not. */
interface LayerAdvancedSettings {
  requiredFields: string[];
  numericFields: string[];
  categoricalFields: string[];
  orphanReferenceLayerId: string;
  orphanSnapToleranceMeters: string;
  relationshipRules: RuleRow[];
  statusField: string;
  allowedStatusValues: string;
  statusCheckConnectivity: boolean;
}

function blankAdvanced(): LayerAdvancedSettings {
  return {
    requiredFields: [],
    numericFields: [],
    categoricalFields: [],
    orphanReferenceLayerId: '',
    orphanSnapToleranceMeters: '5',
    relationshipRules: [],
    statusField: '',
    allowedStatusValues: '',
    statusCheckConnectivity: false,
  };
}

let nextRowId = 0;
function newRowId(): string {
  nextRowId += 1;
  return `rule-${nextRowId}`;
}

/** A single GeoJSON geometry's rough centre, for zoom-to — wraps it in a
 *  one-feature collection to reuse `computeBounds` rather than a second
 *  centroid implementation. */
function centreOf(geometry: GeoJSON.Geometry): [number, number] | null {
  const bounds = computeBounds({
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry, properties: {} }],
  });
  if (!bounds) return null;
  const [west, south, east, north] = bounds;
  return [(west + east) / 2, (south + north) / 2];
}

function toggleInArray(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

export function FindProblemsPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);
  const defaultSnap = cfg<number>(ctx.config, 'defaultSnapToleranceMeters', 0.5);
  const defaultOverlap = cfg<number>(ctx.config, 'defaultOverlapPercent', 1);

  const [selectedLayerIds, setSelectedLayerIds] = useState<string[]>(() => allowed.map((layer) => layer.id));
  const [checks, setChecks] = useState<Set<string>>(
    () => new Set(['suspicious_geometry', 'duplicate_assets', 'overlapping_features', 'disconnected_pipeline', 'abnormal_asset_patterns']),
  );
  const [duplicateMatchMode, setDuplicateMatchMode] = useState<'geometry' | 'geometry_and_attributes'>('geometry');
  const [minOverlapPercent, setMinOverlapPercent] = useState(String(defaultOverlap));
  const [snapToleranceMeters, setSnapToleranceMeters] = useState(String(defaultSnap));
  const [layerSettings, setLayerSettings] = useState<Record<string, LayerAdvancedSettings>>({});
  const [expandedLayerId, setExpandedLayerId] = useState<string | null>(null);
  const [layerFields, setLayerFields] = useState<Record<string, string[]>>({});
  const [layerNumericFields, setLayerNumericFields] = useState<Record<string, string[]>>({});

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<QualityProblem[] | null>(null);
  const [summary, setSummary] = useState<{ layerId: string; layerName: string; problemCount: number; totalFeatures: number }[] | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [resultLayerId, setResultLayerId] = useState<string | null>(null);
  const [filterLayerId, setFilterLayerId] = useState<string>('');

  const hasAdvancedCheck = useMemo(() => Array.from(checks).some((id) => ADVANCED_CHECK_IDS.has(id)), [checks]);

  // Every layer's fields, loaded once — cheap for a file-backed layer
  // (already in memory) and needed both for the layer being scanned AND as
  // the choices in a "which field on the reference layer" picker.
  useEffect(() => {
    let live = true;
    for (const layer of ctx.layers) {
      void fetchLayerFieldNames(layer, ctx.module).then((names) => {
        if (live) setLayerFields((current) => ({ ...current, [layer.id]: [...names].sort() }));
      });
      void fetchNumericFieldNames(layer, ctx.module).then((names) => {
        if (live) setLayerNumericFields((current) => ({ ...current, [layer.id]: [...names].sort() }));
      });
    }
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.layers.map((l) => l.id).join(',')]);

  const settingsFor = (layerId: string): LayerAdvancedSettings => layerSettings[layerId] ?? blankAdvanced();
  const updateLayerSettings = (layerId: string, changes: Partial<LayerAdvancedSettings>) =>
    setLayerSettings((current) => ({ ...current, [layerId]: { ...settingsFor(layerId), ...changes } }));

  const addRule = (layerId: string) =>
    updateLayerSettings(layerId, {
      relationshipRules: [...settingsFor(layerId).relationshipRules, { id: newRowId(), thisField: '', referenceLayerId: '', referenceField: '' }],
    });
  const updateRule = (layerId: string, ruleId: string, changes: Partial<RuleRow>) =>
    updateLayerSettings(layerId, {
      relationshipRules: settingsFor(layerId).relationshipRules.map((rule) => (rule.id === ruleId ? { ...rule, ...changes } : rule)),
    });
  const removeRule = (layerId: string, ruleId: string) =>
    updateLayerSettings(layerId, { relationshipRules: settingsFor(layerId).relationshipRules.filter((rule) => rule.id !== ruleId) });

  const toggleCheck = (id: string) =>
    setChecks((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const buildSettings = (layerId: string): QualityCheckSettings => {
    const advanced = settingsFor(layerId);
    return {
      checks: Array.from(checks),
      duplicateMatchMode,
      requiredFields: advanced.requiredFields,
      minOverlapPercent: Number(minOverlapPercent) || 1,
      snapToleranceMeters: Number(snapToleranceMeters) || 0.5,
      numericFields: advanced.numericFields,
      categoricalFields: advanced.categoricalFields,
      orphanReferenceLayerId: advanced.orphanReferenceLayerId || undefined,
      orphanSnapToleranceMeters: Number(advanced.orphanSnapToleranceMeters) || 5,
      relationshipRules: advanced.relationshipRules
        .filter((rule) => rule.thisField && rule.referenceLayerId && rule.referenceField)
        .map(({ thisField, referenceLayerId, referenceField }) => ({ thisField, referenceLayerId, referenceField })),
      statusField: advanced.statusField || undefined,
      allowedStatusValues: advanced.allowedStatusValues
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
      statusCheckConnectivity: advanced.statusCheckConnectivity,
    };
  };

  const buildProblemLayer = (list: QualityProblem[]): GeoJSON.FeatureCollection => ({
    type: 'FeatureCollection',
    features: list
      .filter((problem) => problem.geometry)
      .map((problem) => ({
        type: 'Feature',
        geometry: problem.geometry as GeoJSON.Geometry,
        properties: {
          severity: problem.severity,
          check: problem.check,
          message: problem.message,
          layerName: problem.layerName,
        },
      })),
  });

  const problemsRenderer: LayerRenderer = {
    type: 'unique',
    color: SEVERITY_COLOR.info,
    fillOpacity: 0.85,
    outlineColor: '#ffffff',
    outlineWidth: 2,
    pointSize: 9,
    field: 'severity',
    categories: [
      { value: 'critical', label: 'Critical', color: SEVERITY_COLOR.critical },
      { value: 'warning', label: 'Warning', color: SEVERITY_COLOR.warning },
      { value: 'info', label: 'Info', color: SEVERITY_COLOR.info },
    ],
    defaultColor: SEVERITY_COLOR.info,
  };

  const run = async () => {
    if (selectedLayerIds.length === 0) {
      setError('Choose at least one layer to scan.');
      return;
    }
    if (checks.size === 0) {
      setError('Choose at least one check to run.');
      return;
    }
    setError(null);
    setBusy(true);
    setActiveIndex(null);
    try {
      const result = await qualityCheckService.run(
        ctx.module.id,
        selectedLayerIds.map((layerId) => ({ layerId, settings: buildSettings(layerId) })),
      );
      const sorted = [...result.problems].sort(
        (a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9),
      );
      setProblems(sorted);
      setSummary(result.summary);
      setFilterLayerId('');

      const geojson = buildProblemLayer(sorted);
      if (geojson.features.length > 0) {
        if (resultLayerId && ctx.layers.some((layer) => layer.id === resultLayerId) && ctx.onLayerChange) {
          ctx.onLayerChange(resultLayerId, { localData: geojson, bounds: computeBounds(geojson) });
        } else {
          const newId = ctx.onCreateTempLayer?.('Problems', geojson, 'Find Problems', problemsRenderer);
          setResultLayerId(newId ?? null);
        }
      }
    } catch (failure) {
      console.error('[findProblems] failed', failure);
      setError(failure instanceof Error ? failure.message : 'Could not run this scan.');
    } finally {
      setBusy(false);
    }
  };

  /** Every result, one row per flagged feature — its problem info AND its
   *  own attributes, side by side, so this can be handed to a field crew or
   *  opened straight in Excel without a second lookup back into the layer.
   *  Always exports the FULL result set, not just what a layer filter is
   *  currently narrowing the list to — "export" should never silently be
   *  smaller than what the summary counts just promised. */
  const exportCsv = () => {
    if (!problems || problems.length === 0) return;

    const attributeKeys = Array.from(new Set(problems.flatMap((problem) => Object.keys(problem.properties || {})))).sort();
    const detailKeys = Array.from(new Set(problems.flatMap((problem) => Object.keys(problem.detail || {})))).sort();
    const columns = [
      'Layer',
      'Problem',
      'Severity',
      'Row',
      'Message',
      ...attributeKeys.map((key) => `attr: ${key}`),
      ...detailKeys.map((key) => `detail: ${key}`),
    ];

    const format = (value: unknown): string => {
      if (value === null || value === undefined) return '';
      if (Array.isArray(value)) return value.map((entry) => format(entry)).join('; ');
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };
    const cell = (value: unknown) => `"${format(value).replace(/"/g, '""')}"`;
    const checkLabel = (id: string) => CHECK_DEFS.find((entry) => entry.id === id)?.label ?? id;

    const rows = problems.map((problem) => [
      problem.layerName,
      checkLabel(problem.check),
      SEVERITY_LABEL[problem.severity] ?? problem.severity,
      problem.featureIndex !== null ? problem.featureIndex + 1 : '',
      problem.message,
      ...attributeKeys.map((key) => (problem.properties || {})[key]),
      ...detailKeys.map((key) => (problem.detail || {})[key]),
    ]);

    // A leading BOM so Excel (which otherwise guesses the wrong encoding for
    // anything outside plain ASCII) opens this as UTF-8 instead of mangling it.
    const csv = '﻿' + [columns.map(cell).join(','), ...rows.map((row) => row.map(cell).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `find-problems-${ctx.module.id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const visibleProblems = useMemo(
    () => (problems ?? []).filter((problem) => !filterLayerId || problem.layerId === filterLayerId),
    [problems, filterLayerId],
  );

  const goToProblem = (problem: QualityProblem) => {
    if (!problem.geometry) return;
    const centre = centreOf(problem.geometry);
    if (!centre) return;
    ctx.onGoToLocation([centre[1], centre[0]], Math.max(ctx.mapZoom, 18));
  };

  const openProblem = (problem: QualityProblem) => {
    const index = (problems ?? []).findIndex((entry) => entry.id === problem.id);
    setActiveIndex(index >= 0 ? index : null);
    goToProblem(problem);
  };

  const step = (delta: number) => {
    if (!problems || problems.length === 0) return;
    const pool = filterLayerId ? problems.filter((p) => p.layerId === filterLayerId) : problems;
    if (pool.length === 0) return;
    const currentPoolIndex = activeIndex === null ? -1 : pool.findIndex((p) => p.id === problems[activeIndex]?.id);
    const nextPoolIndex = (currentPoolIndex + delta + pool.length) % pool.length;
    const nextProblem = pool[nextPoolIndex];
    const nextGlobalIndex = problems.findIndex((p) => p.id === nextProblem.id);
    setActiveIndex(nextGlobalIndex);
    goToProblem(nextProblem);
  };

  const activeProblem = activeIndex !== null && problems ? problems[activeIndex] : null;

  const clearResults = () => {
    setProblems(null);
    setSummary(null);
    setActiveIndex(null);
    setError(null);
  };

  return (
    <>
      <ToolPanelHeader
        title="Find Problems"
        description="Scan layers for data-quality issues, then step through them one by one."
        onClose={ctx.onClose}
      />

      {allowed.length === 0 ? (
        <EmptyHint>This map has no layers this tool is allowed to scan yet.</EmptyHint>
      ) : (
        <>
          <Field label="Layers to scan" hint="Only file-backed and shared layers are checked — live services are out of scope for this tool.">
            <ScrollList max={120}>
              {allowed.map((layer) => (
                <label key={layer.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12.5px]">
                  <input
                    type="checkbox"
                    checked={selectedLayerIds.includes(layer.id)}
                    onChange={() => setSelectedLayerIds((current) => toggleInArray(current, layer.id))}
                  />
                  <span className="truncate">{getLayerDisplayName(layer)}</span>
                </label>
              ))}
            </ScrollList>
          </Field>

          <Field label="Checks to run">
            <div className="space-y-1">
              {CHECK_DEFS.map((check) => (
                <label key={check.id} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={checks.has(check.id)}
                    onChange={() => toggleCheck(check.id)}
                  />
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-medium text-slate-700">
                      {check.label}
                      {check.advanced && (
                        <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-amber-700">
                          needs setup
                        </span>
                      )}
                    </span>
                    <span className="block text-[10.5px] leading-snug text-slate-400">{check.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </Field>

          {(checks.has('overlapping_features') || checks.has('disconnected_pipeline') || checks.has('inconsistent_status') || checks.has('duplicate_assets')) && (
            <FieldRow>
              {checks.has('duplicate_assets') && (
                <Field label="Duplicates match by">
                  <select
                    value={duplicateMatchMode}
                    onChange={(event) => setDuplicateMatchMode(event.target.value as typeof duplicateMatchMode)}
                    className={inputClass}
                  >
                    <option value="geometry">Geometry only</option>
                    <option value="geometry_and_attributes">Geometry and attributes</option>
                  </select>
                </Field>
              )}
              {checks.has('overlapping_features') && (
                <Field label="Min. overlap to flag (%)">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={minOverlapPercent}
                    onChange={(event) => setMinOverlapPercent(event.target.value)}
                    className={inputClass}
                  />
                </Field>
              )}
              {(checks.has('disconnected_pipeline') || checks.has('inconsistent_status')) && (
                <Field label="Connectivity snap tolerance (m)">
                  <input
                    type="number"
                    min={0.01}
                    value={snapToleranceMeters}
                    onChange={(event) => setSnapToleranceMeters(event.target.value)}
                    className={inputClass}
                  />
                </Field>
              )}
            </FieldRow>
          )}

          {hasAdvancedCheck && (
            <Field
              label="Per-layer setup"
              hint="Missing attributes, orphan features, invalid relationships and inconsistent status each need to know which field or which other layer to check against."
            >
              <div className="space-y-1.5">
                {allowed
                  .filter((layer) => selectedLayerIds.includes(layer.id))
                  .map((layer) => {
                    const advanced = settingsFor(layer.id);
                    const open = expandedLayerId === layer.id;
                    const fields = layerFields[layer.id] ?? [];
                    const numericFields = layerNumericFields[layer.id] ?? [];
                    return (
                      <div key={layer.id} className="rounded-lg border border-slate-200 bg-white">
                        <button
                          type="button"
                          onClick={() => setExpandedLayerId(open ? null : layer.id)}
                          className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[12.5px] font-medium text-slate-700"
                        >
                          {open ? <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />}
                          <span className="truncate">{getLayerDisplayName(layer)}</span>
                        </button>
                        {open && (
                          <div className="space-y-2.5 border-t border-slate-100 px-2.5 py-2.5">
                            {checks.has('missing_attributes') && (
                              <Field label="Required fields">
                                <div className="flex flex-wrap gap-1">
                                  {fields.length === 0 && <span className="text-[11px] text-slate-400">No fields found.</span>}
                                  {fields.map((field) => (
                                    <button
                                      type="button"
                                      key={field}
                                      onClick={() =>
                                        updateLayerSettings(layer.id, { requiredFields: toggleInArray(advanced.requiredFields, field) })
                                      }
                                      className={`rounded-full border px-2 py-0.5 text-[11px] ${
                                        advanced.requiredFields.includes(field)
                                          ? 'border-blue-300 bg-blue-50 text-blue-700'
                                          : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                      }`}
                                    >
                                      {field}
                                    </button>
                                  ))}
                                </div>
                              </Field>
                            )}

                            {checks.has('abnormal_asset_patterns') && (
                              <>
                                <Field label="Numeric fields to check for outliers">
                                  <div className="flex flex-wrap gap-1">
                                    {numericFields.length === 0 && <span className="text-[11px] text-slate-400">No numeric fields found.</span>}
                                    {numericFields.map((field) => (
                                      <button
                                        type="button"
                                        key={field}
                                        onClick={() =>
                                          updateLayerSettings(layer.id, { numericFields: toggleInArray(advanced.numericFields, field) })
                                        }
                                        className={`rounded-full border px-2 py-0.5 text-[11px] ${
                                          advanced.numericFields.includes(field)
                                            ? 'border-blue-300 bg-blue-50 text-blue-700'
                                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                        }`}
                                      >
                                        {field}
                                      </button>
                                    ))}
                                  </div>
                                </Field>
                                <Field label="Text fields to check for rare/typo values">
                                  <div className="flex flex-wrap gap-1">
                                    {fields.map((field) => (
                                      <button
                                        type="button"
                                        key={field}
                                        onClick={() =>
                                          updateLayerSettings(layer.id, { categoricalFields: toggleInArray(advanced.categoricalFields, field) })
                                        }
                                        className={`rounded-full border px-2 py-0.5 text-[11px] ${
                                          advanced.categoricalFields.includes(field)
                                            ? 'border-blue-300 bg-blue-50 text-blue-700'
                                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                        }`}
                                      >
                                        {field}
                                      </button>
                                    ))}
                                  </div>
                                </Field>
                              </>
                            )}

                            {checks.has('orphan_features') && (
                              <FieldRow>
                                <Field label="Must be near this layer">
                                  <select
                                    value={advanced.orphanReferenceLayerId}
                                    onChange={(event) => updateLayerSettings(layer.id, { orphanReferenceLayerId: event.target.value })}
                                    className={inputClass}
                                  >
                                    <option value="">Choose a layer…</option>
                                    {ctx.layers
                                      .filter((candidate) => candidate.id !== layer.id)
                                      .map((candidate) => (
                                        <option key={candidate.id} value={candidate.id}>
                                          {getLayerDisplayName(candidate)}
                                        </option>
                                      ))}
                                  </select>
                                </Field>
                                <Field label="Within (m)">
                                  <input
                                    type="number"
                                    min={0.01}
                                    value={advanced.orphanSnapToleranceMeters}
                                    onChange={(event) => updateLayerSettings(layer.id, { orphanSnapToleranceMeters: event.target.value })}
                                    className={inputClass}
                                  />
                                </Field>
                              </FieldRow>
                            )}

                            {checks.has('invalid_relationships') && (
                              <Field
                                label="Relationship rules"
                                hint="A field here must match a field's value somewhere in the chosen reference layer."
                              >
                                <div className="space-y-1.5">
                                  {advanced.relationshipRules.map((rule) => {
                                    const refFields = layerFields[rule.referenceLayerId] ?? [];
                                    return (
                                      <div key={rule.id} className="space-y-1 rounded-md border border-slate-100 bg-slate-50 p-1.5">
                                        <div className="flex items-center gap-1.5">
                                          <select
                                            value={rule.thisField}
                                            onChange={(event) => updateRule(layer.id, rule.id, { thisField: event.target.value })}
                                            className={`${inputClass} flex-1`}
                                          >
                                            <option value="">This field…</option>
                                            {fields.map((field) => (
                                              <option key={field} value={field}>{field}</option>
                                            ))}
                                          </select>
                                          <span className="flex-shrink-0 text-[11px] text-slate-400">must be in</span>
                                          <button
                                            type="button"
                                            onClick={() => removeRule(layer.id, rule.id)}
                                            aria-label="Remove rule"
                                            className="flex-shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                          >
                                            <RemoveIcon className="h-3.5 w-3.5" />
                                          </button>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          <select
                                            value={rule.referenceLayerId}
                                            onChange={(event) =>
                                              updateRule(layer.id, rule.id, { referenceLayerId: event.target.value, referenceField: '' })
                                            }
                                            className={`${inputClass} flex-1`}
                                          >
                                            <option value="">Reference layer…</option>
                                            {ctx.layers
                                              .filter((candidate) => candidate.id !== layer.id)
                                              .map((candidate) => (
                                                <option key={candidate.id} value={candidate.id}>{getLayerDisplayName(candidate)}</option>
                                              ))}
                                          </select>
                                          <select
                                            value={rule.referenceField}
                                            onChange={(event) => updateRule(layer.id, rule.id, { referenceField: event.target.value })}
                                            disabled={!rule.referenceLayerId}
                                            className={`${inputClass} flex-1`}
                                          >
                                            <option value="">Reference field…</option>
                                            {refFields.map((field) => (
                                              <option key={field} value={field}>{field}</option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  <button
                                    type="button"
                                    onClick={() => addRule(layer.id)}
                                    className="flex items-center gap-1 text-[11.5px] font-medium text-blue-600 hover:text-blue-700"
                                  >
                                    <Plus className="h-3.5 w-3.5" /> Add rule
                                  </button>
                                </div>
                              </Field>
                            )}

                            {checks.has('inconsistent_status') && (
                              <>
                                <FieldRow>
                                  <Field label="Status field">
                                    <select
                                      value={advanced.statusField}
                                      onChange={(event) => updateLayerSettings(layer.id, { statusField: event.target.value })}
                                      className={inputClass}
                                    >
                                      <option value="">Choose a field…</option>
                                      {fields.map((field) => (
                                        <option key={field} value={field}>{field}</option>
                                      ))}
                                    </select>
                                  </Field>
                                </FieldRow>
                                <Field label="Allowed values" hint="Comma-separated. Leave blank to skip this part of the check.">
                                  <input
                                    type="text"
                                    placeholder="Active, Inactive, Abandoned, Proposed"
                                    value={advanced.allowedStatusValues}
                                    onChange={(event) => updateLayerSettings(layer.id, { allowedStatusValues: event.target.value })}
                                    className={inputClass}
                                  />
                                </Field>
                                <label className="flex items-center gap-2 text-[12px] text-slate-600">
                                  <input
                                    type="checkbox"
                                    checked={advanced.statusCheckConnectivity}
                                    onChange={(event) => updateLayerSettings(layer.id, { statusCheckConnectivity: event.target.checked })}
                                  />
                                  Also flag when two directly-connected segments have different status
                                </label>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </Field>
          )}

          <ToolActions>
            <button type="button" onClick={() => void run()} disabled={busy} className={`${btnClass} gl-btn--primary`}>
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {busy ? 'Scanning…' : 'Run'}
            </button>
            <button type="button" onClick={clearResults} disabled={!problems} className={`${btnClass} gl-btn--secondary`}>
              Reset
            </button>
          </ToolActions>

          {error && <Notice tone="warn">{error}</Notice>}

          {problems && summary && (
            <div className="space-y-2">
              <Notice>
                {problems.length === 0
                  ? 'No problems found in the layers scanned.'
                  : `${problems.length.toLocaleString()} problem${problems.length === 1 ? '' : 's'} found across ${summary.filter((s) => s.problemCount > 0).length} layer${summary.filter((s) => s.problemCount > 0).length === 1 ? '' : 's'}.`}
                {problems.length > 0 && ' Also added to the map as a temporary "Problems" layer, colour-coded by severity.'}
              </Notice>

              {problems.length > 0 && (
                <button
                  type="button"
                  onClick={exportCsv}
                  className={`${btnClass} gl-btn--secondary`}
                >
                  <Download className="h-3.5 w-3.5" />
                  Export to CSV / Excel
                </button>
              )}

              {summary.length > 1 && (
                <Field label="Filter by layer">
                  <select value={filterLayerId} onChange={(event) => setFilterLayerId(event.target.value)} className={inputClass}>
                    <option value="">All layers ({problems.length})</option>
                    {summary.map((entry) => (
                      <option key={entry.layerId} value={entry.layerId}>
                        {entry.layerName} ({entry.problemCount})
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              {summary.some((entry) => entry.problemCount > 0) && (
                <ul className="space-y-0.5">
                  {summary
                    .filter((entry) => entry.problemCount > 0)
                    .map((entry) => (
                      <li key={entry.layerId} className="flex items-center justify-between text-[11.5px] text-slate-600">
                        <span className="truncate">{entry.layerName}</span>
                        <span className="tabular-nums font-semibold text-slate-700">{entry.problemCount}</span>
                      </li>
                    ))}
                </ul>
              )}

              {visibleProblems.length > 0 && (
                <>
                  <ResultHeader label="Flagged features" count={visibleProblems.length} />
                  <ScrollList max={260}>
                    {visibleProblems.map((problem) => (
                      <ResultRow
                        key={problem.id}
                        title={problem.message}
                        detail={`${problem.layerName} · ${SEVERITY_LABEL[problem.severity] ?? problem.severity}`}
                        active={activeProblem?.id === problem.id}
                        onClick={() => openProblem(problem)}
                        action={
                          <span
                            className="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-white"
                            style={{ backgroundColor: SEVERITY_COLOR[problem.severity] ?? SEVERITY_COLOR.info }}
                          >
                            {SEVERITY_LABEL[problem.severity] ?? problem.severity}
                          </span>
                        }
                      />
                    ))}
                  </ScrollList>
                </>
              )}

              {activeProblem && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-[11.5px] font-semibold text-blue-800">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {activeProblem.layerName}
                      {activeProblem.featureIndex !== null && ` — row ${activeProblem.featureIndex + 1}`}
                    </span>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => step(-1)} className="rounded p-1 text-blue-700 hover:bg-blue-100">
                        ‹ Prev
                      </button>
                      <button type="button" onClick={() => step(1)} className="rounded p-1 text-blue-700 hover:bg-blue-100">
                        Next ›
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 text-[12px] text-blue-900">{activeProblem.message}</p>
                  {Object.keys(activeProblem.detail).length > 0 && (
                    <dl className="mt-1.5 space-y-0.5">
                      {Object.entries(activeProblem.detail).map(([key, value]) => (
                        <div key={key} className="flex gap-1.5 text-[11px]">
                          <dt className="font-medium text-blue-700">{key}:</dt>
                          <dd className="min-w-0 truncate text-blue-900">{Array.isArray(value) ? value.join(', ') : String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {activeProblem.geometry && (
                    <button
                      type="button"
                      onClick={() => goToProblem(activeProblem)}
                      className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900"
                    >
                      <Crosshair className="h-3 w-3" /> Zoom to this feature
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

export default FindProblemsPanel;
